/**
 * The one way a UI test reaches a component. Wrap ONLY the part that changes:
 *
 *   <UiTest test="thumb-location" arms={{
 *     control: () => <VerifiedBadge />,
 *     location: () => <ThumbLocation text={locationText} />,
 *   }} />
 *
 * Every arm in utils/ui-tests/registry.ts must be given, and no other; the
 * types refuse anything else. Rendering it counts this visitor as exposed
 * (once per visit), unless `exposed={false}` says the arm does not change what
 * they see. Until the arm is known it renders control and counts nothing.
 */
import React, { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useUiTests } from '../../contexts/UiTestProvider';
import { armsOf, type UiArmOf, type UiTestId } from '../../utils/ui-tests/registry';

/** Arms pinned by `ForceUiTestArm`, for previews on the dash. */
const PinnedArms = createContext<Readonly<Record<string, string>>>({});

/**
 * The arm this visitor gets, or null while it is not yet known (first visit,
 * before the server answers). A pinned or forced arm wins and is never counted.
 */
export function useUiTestArm<T extends UiTestId>(test: T, options: { exposed?: boolean } = {}): UiArmOf<T> | null {
  const { served, forced } = useUiTests();
  const pinned = useContext(PinnedArms)[test];
  const known = armsOf(test);
  const valid = (arm: string | undefined): arm is UiArmOf<T> => arm !== undefined && known.includes(arm);

  let arm: UiArmOf<T> | null;
  if (valid(pinned)) arm = pinned;
  else if (valid(forced[test])) arm = forced[test] as UiArmOf<T>;
  else if (served === null) arm = null;
  else arm = (valid(served[test]?.arm) ? served[test].arm : 'control') as UiArmOf<T>;

  useUiTestExposure(test, options.exposed !== false && !valid(pinned) && arm !== null);
  return arm;
}

/**
 * Count this visitor in a test when `when` is true, once per visit. For a
 * caller that reads the arm with `exposed: false` and decides later whether
 * the arm reached the screen. A pinned preview or a forced arm never counts
 * (the provider refuses those), nor does anything before the server answered.
 */
export function useUiTestExposure(test: UiTestId, when: boolean): void {
  const { settled, recordExposure } = useUiTests();
  const pinned = useContext(PinnedArms)[test];
  const counts = when && settled && pinned === undefined;
  useEffect(() => {
    if (counts) recordExposure(test);
  }, [counts, recordExposure, test]);
}

export interface UiTestProps<T extends UiTestId> {
  test: T;
  arms: { [A in UiArmOf<T>]: () => ReactNode };
  /** False when this visitor's view does not depend on the arm, so they must not be counted. */
  exposed?: boolean;
}

export function UiTest<T extends UiTestId>({ test, arms, exposed }: UiTestProps<T>) {
  const arm = useUiTestArm(test, { exposed });
  const render = arms[(arm ?? 'control') as UiArmOf<T>];
  return <>{render()}</>;
}

/**
 * After a test is promoted and the code keeps only the winner: renders that,
 * and still counts the visitor while the dash tracks whether the lift held.
 * Keep it until the dash shows the tracking ended.
 */
export function UiTestShipped({ test, children }: { test: UiTestId; children: ReactNode }) {
  useUiTestArm(test);
  return children;
}

/** Show one arm inside, whatever the visitor gets; never counted. For the dash's arm previews. */
export function ForceUiTestArm<T extends UiTestId>({ test, arm, children }: { test: T; arm: UiArmOf<T>; children: ReactNode }) {
  const outer = useContext(PinnedArms);
  return <PinnedArms.Provider value={{ ...outer, [test]: arm }}>{children}</PinnedArms.Provider>;
}
